import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import * as Sentry from "@sentry/browser";

import {
  selectCurrentUser,
  selectEnqueueSnackbar,
  selectIsConnected,
  selectWsInit,
} from "../selectors/app.selectors";
import {
  APP_LOGIN_ADMIN,
  APP_LOGOUT,
  APP_MOUNTED,
  CHECK_JWT,
  CONNECTION_CHANGED,
  ENQUEUE_SNACKBAR,
  FORCE_RELOAD,
  GET_DATA,
  KEEP_ALIVE,
  LANGUAGE_CHANGED,
  NEW_ADMIN_TOKEN,
  NEW_TOKEN,
  RECONNECT_WEBSOCKET,
  RETRY_GET_DATA,
  SET_IS_ONLINE,
  USER_CHANGED,
  WS_DATA_INIT,
} from "../constants/app.constants";
import { MyWebSocket } from "../../utils/MyWebSocket";
import BaseAPI from "../../utils/API/BaseAPI";
import { deviceSagas } from "./device.saga";
import { alarmSagas } from "./alarm.saga";
import { dashboardSagas } from "./dashboard.saga";
import { teamSagas } from "./team.saga";
import { scenarioSagas } from "./scenario.saga";
import { accountSagas } from "./account.saga";
import { geofenceSagas } from "./geofence.saga";
import { beaconSagas } from "./beacon.saga";
import { siteSagas } from "./site.saga";
import { eventSagas } from "./event.saga";
import { administrationSagas } from "./administration.saga";
import { assistanceRequestSagas } from "./assistanceRequest.saga";
import User from "../../utils/models/User";
import { AdminAPI } from "../../utils/API/AdminAPI";
import {
  dataReceived,
  logout as doLogout,
  newToken,
  retryGetData as retryGetDataAction,
  setAppEnabled,
  toggleDisconnectedSound,
  userChanged,
  v4LocalesLoaded,
  v4NativeBootFinished,
  websocketInitialized,
} from "../actions/app.actions";
import { UserTypes } from "../../utils/models/UserType";
import { moduleIds } from "../../utils/models/Module";
import {
  mapV4DeviceToV3,
  mapV4EventToV3,
} from "../../containers/V4ARC/transforms";
import i18next from "i18next";
import { UserAPI } from "../../utils/API/UserAPI";
import dayjs from "dayjs";
import {
  WS_DATA_ALARM,
  WS_DATA_ALARMS,
  WS_DATA_ASS_REQUEST,
  WS_DATA_ASS_REQUESTS,
  WS_FORCE_RELOAD,
  WS_REQUEST_RELOAD_DATA,
} from "../constants/alarm.constants";
import { showAlert } from "../../containers/AlertDialog/actions";
import { getI18n } from "react-i18next";
import { getActivePage, getLangFromLocale, uniqueArray } from "../../utils/functions";
import jwtDecode from "jwt-decode";
import { getStore } from "../../configureStore";
import { getWhitelabel, isLive } from "../../env";
import { AUTH_SUCCESS } from "../../containers/LoginPage/constants";
import { getNavigator } from "../../Navigator";
import { selectOpenAlertAlarms } from "../selectors/alarm.selector";
import { selectOpenAssistanceRequests } from "../selectors/assistanceRequest.selector";
import { selectOptions } from "../../containers/PresentationPage/selectors";
import { userSagas } from "./user.saga";
import { authSuccess } from "../../containers/LoginPage/actions";
import { subscribeToWsTopics } from "../actions/dashboard.actions";
import { consoleDebug, consoleWarn } from "../../utils/log";
import { attachV4Locations, deviceV4Sagas, fetchV4OnlyDevicesSaga, fetchV4OnlyDevicesForUserSaga, fetchV4DeviceTypesSaga } from "./deviceV4.saga";
import { alarmV4Sagas } from "./alarmV4.saga";
import { scenarioV4Sagas } from "./scenarioV4.saga";
import v4api, {clearV4Session, getV4AccountUuid, getV4Permissions, getV4SessionScope, isOnV4ExcludedPath, leaveV4SessionScope, mapV4ContactToV3, setV4BootScope, setV4OwnerAccount, v4Unscoped} from "../../api/V4client";
import {getV4Token} from "../../utils/v4Session";
import {clearOrphanedV3Session} from "../../utils/v3Session";
import {ownsV4Accounts, v4ArcOnlyRole} from "../../utils/v4Permissions";
import {isV4ManagerBoot, v4BootManagesAccounts} from "./v4BootRole";
import { SET_SELECTED_ACCOUNT } from "../../containers/AdminPage/constants";
import { ACCOUNT_FILTERS_KEY } from "../../containers/AccountPage/constants";

global.toPage = (page) => {
  if (isLive()) return;
  getNavigator()(page);
};
global.myDispatch = (action) => {
  if (isLive()) return;
  getStore().dispatch(action);
};

const api = new BaseAPI();
const adminApi = new AdminAPI();
const userApi = new UserAPI();

export function* saveUser({ store }) {
  const user = yield select(selectCurrentUser());
  if (store !== false && !BaseAPI.inMemoryToken) {
    localStorage.setItem("user", JSON.stringify(user.toJS()));
  } else {
    // V4client renews a V4-native token in v4User; the record still holds the boot copy.
    BaseAPI.inMemoryToken = (user.isV4Native && getV4Token()) || user.token;
  }
}

function* loadWebSocket(props) {
  const force = !!props?.force;
  const delayMs = props?.delayMs || 0;
  const websocket = MyWebSocket.inst();
  const isOpen = yield websocket?.isOpen();
  if (isOpen && !force) return;
  yield delay(delayMs || 0);
  const user = yield select(selectCurrentUser());
  if (user?.isV4Public || user?.isV4Native) return;
  if (user) yield loginWebSocket();
}

function* loginWebSocket() {
  const user = yield select(selectCurrentUser());
  if (user?.isV4Public || user?.isV4Native) return;
  yield MyWebSocket.inst().restart();
  MyWebSocket.inst().subscribe("requestReloadData");
  const page = yield select(selectOptions());
  if (user.isPresentation) {
    yield put(subscribeToWsTopics());
    return;
  }
  if (!page) {
    MyWebSocket.inst().subscribe("*");
  }
}

let dataRetryTimeout = null;

function* connectionChanged({ isConnected }) {
  if (dataRetryTimeout) clearTimeout(dataRetryTimeout);
  const user: User = yield select(selectCurrentUser());
  if (user?.isV4Public || user?.isV4Native) return;
  if (isConnected && user) {
    yield delay(300);
    yield api.requestData();
    dataRetryTimeout = setTimeout(() => {
      getStore().dispatch(retryGetDataAction());
    }, 3000);
  }
}

function* retryGetData() {
  const wsInit = yield select(selectWsInit());
  if (!wsInit) {
    yield getData();
  }
}

function* getData() {
  const user: User = yield select(selectCurrentUser());
  if (user?.isV4Native) return;
  if (user.isUser || user.isPresentation) {
    yield api.requestData();
  }
}

function* redirectUser() {
  const user: User = yield select(selectCurrentUser());
  if (user && !user.lastLogin && !user.isPresentation) {
    getNavigator()("/newPassword");
    return;
  }
  // No user yet — the admin check below would bounce them; AUTH_SUCCESS re-runs this.
  if (!user) return;
  const page = getActivePage();
  if (page.indexOf("admin") >= 0 && !user.isSuperAdmin && !user.isAccountManager) {
    return getNavigator()("/");
  }
}

function clearAccountFilters() {
  const prefix = `${ACCOUNT_FILTERS_KEY}:`;
  const perManager = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(prefix)) perManager.push(key);
  }
  [ACCOUNT_FILTERS_KEY, ...perManager].forEach((key) => sessionStorage.removeItem(key));
}

function* logout({ isExpired }) {
  yield Promise.resolve();
  if (MyWebSocket.inst()) {
    MyWebSocket.inst().close();
  }
  const wasAdmin = !!localStorage.getItem("adminUser");
  localStorage.removeItem("user");
  localStorage.removeItem("adminUser");
  localStorage.removeItem("accountManagerUser");
  localStorage.removeItem("agreedToTermsOfUse");
  localStorage.removeItem("rt");
  clearAccountFilters();
  // A leftover V4 token would override the next V3 session's token.
  clearV4Session();
  // A V4-native session's token lives in memory, not in storage.
  BaseAPI.inMemoryToken = null;
  if (isExpired) {
    const user = yield select(selectCurrentUser());
    const wasPresentation = user.isPresentation;
    //because of potential javascript errors, reload the page
    if (wasAdmin) {
      window.location.href = "/?msg=adminSessionExpiredMessage&type=warning";
    } else if (wasPresentation) {
      window.location.reload();
    } else {
      window.location.href = "/?msg=sessionExpiredMessage&type=warning";
    }
  } else {
    window.location.href = "/";
  }
}

function* loginAdmin() {
  const user = yield select(selectCurrentUser());
  // Leaving the pinned scope is the whole "logout" from a scoped session, one level at a time.
  if (user?.isV4Native) {
    leaveV4SessionScope();
    window.location.href = "/";
    return;
  }
  localStorage.removeItem("agreedToTermsOfUse");
  yield adminApi.loginAdmin();
  if (user?.isUser) {
    window.location.href = `/admin/devices/${user.accountId}`;
  } else {
    window.location.href = `/`;
  }
}
function* setLanguage() {
  const user = yield select(selectCurrentUser());
  const newLang = user.get("language");
  if (i18next.language !== newLang) {
    yield i18next.changeLanguage(getLangFromLocale(newLang));
  }
}

function* saveLanguage({ language, callback }) {
  const lang = getLangFromLocale(language);
  dayjs.locale(lang);
  const user = yield select(selectCurrentUser());
  if (user && !user.viaAdmin && !user.isV4Native) {
    yield userApi.saveLanguage(language);
  }
  if (callback) callback();
  if (i18next.language !== language) {
    yield i18next.changeLanguage(lang);
  }
}

function* loadV4Locales({ reloadTranslations = false } = {}) {
  try {
    const res = yield v4api.localesList();
    yield put(v4LocalesLoaded(res?.data || []));
    // A login's first translations load went out before its session existed.
    if (reloadTranslations && typeof i18next.reloadResources === "function") {
      yield i18next.reloadResources();
    }
  } catch (e) {
    consoleWarn("Failed to load V4 locales", e);
  }
}

function* requestForceReload({ doLogout }) {
  yield adminApi.forceReload(doLogout);
}

function* forceReload({ data }) {
  const i18n = getI18n();
  let text = i18n.t("forceReloadWarning.1");
  if (+data === 1) {
    text += `\n${i18n.t("forceReloadWarning.2")}`;
  }
  yield put(
    showAlert(text, i18n.t("reload"), {
      disableBackdropClick: true,
      confirmText: i18n.t("reload"),
      onConfirm: () => {
        if (+data === 1) {
          put(doLogout());
        }
        window.location.reload();
      },
    })
  );
}

function* doEnqueueSnackbar({ input, attrs }) {
  const enqueueSnackbar = yield select(selectEnqueueSnackbar());
  enqueueSnackbar(input, attrs);
}

function* checkForAlert() {
  const urlParams = new URLSearchParams(window.location.search);
  const alert = urlParams.get("alert");
  if (alert) {
    getNavigator()(window.location.pathname);
    const i18n = getI18n();
    yield put(showAlert(i18n.t(alert), i18n.t("alert")));
  }
}

function* checkJWT() {
  const user = yield select(selectCurrentUser());
  if (!user) return;
  // V4 tokens have their own lifecycle; the V3 refresh/reconnect below
  // doesn't apply.
  if (user.isV4Native) return;
  const token = BaseAPI.token;
  try {
    let { exp } = jwtDecode(token);
    if (exp < dayjs().unix()) {
      if (localStorage.getItem("rememberMe") === "yes") {
        yield put(setAppEnabled(false));
        yield api.refreshJWT();
      } else {
        yield put(doLogout(true));
        return;
      }
    }
  } catch (_) {
  } finally {
    yield put(setAppEnabled(true));
  }
  const isConnected = yield select(selectIsConnected());
  if (!isConnected) {
    yield loginWebSocket();
  }
}

function* keepAlive() {
  const user = yield select(selectCurrentUser());
  if (!user || user.isV4Native) return;
  try {
    const { token, adminToken } = yield api.keepAlive();
    if (!token) return;
    yield put(newToken(token));
    if (adminToken) {
      yield saveNewAdminToken({ token: adminToken });
    }
  } catch (_) {
    debugger;
  }
}

function* saveNewToken({ token }) {
  const decoded = jwtDecode(token);
  consoleDebug("new token: " + token);
  decoded.user.token = token;
  let user = new User(decoded.user);
  // The V3 JWT doesn't carry the V4-fetched settings — carry them over so
  // isV4Only / locationMaxValidity survive routine token refreshes.
  const currentUser = yield select(selectCurrentUser());
  if (currentUser && currentUser.v4AccountSettings) {
    user = user.set("v4AccountSettings", currentUser.v4AccountSettings);
  }
  localStorage.setItem("user", JSON.stringify(user.toJS()));
  yield put(userChanged(user));
  MyWebSocket.inst().token = token;
}

function* saveNewAdminToken({ token }) {
  yield Promise.resolve();
  const decoded = jwtDecode(token);
  consoleDebug("new admin token: " + token);
  decoded.user.token = token;
  const user = new User(decoded.user);
  localStorage.setItem("adminUser", JSON.stringify(user.toJS()));
}

function* reconnectWsAfterMount() {
  yield delay(1000);
  yield loadWebSocket();
}

function* identifySentry() {
  try {
    const user = yield select(selectCurrentUser());
    if (!user) return;
    let username = user.email;
    const shadowUser = localStorage.getItem("adminUser");
    if (shadowUser) {
      const type = user.isShadowAccountManager
        ? "Account manager"
        : "SuperAdmin";
      const userObj = JSON.parse(shadowUser);
      username = `${user.accountName} via ${userObj.email} (${type})`;
    }
    Sentry.setUser({ username });
  } catch (e) {
    consoleWarn("could not login sentry");
    consoleWarn(e);
  }
}

export function* refreshDocumentTitle() {
  const user = yield select(selectCurrentUser());
  if (!user) return;
  const i18n = getI18n();
  const isConnected = select(selectIsConnected());
  if (!isConnected || !navigator.onLine) {
    return (document.title = `!! ${i18n.t("connectionLost")} !!`);
  }
  if (!user.isPresentation) {
    // V3 users' V4 assistance requests fall to the pendingAssistanceRequests title below.
    const openAlarms = yield select(selectOpenAlertAlarms());
    if (!!openAlarms.size) {
      return (document.title = `!! ${i18n.t("openAlarms")} !!`);
    }
    const openAssistanceRequests = yield select(selectOpenAssistanceRequests());
    if (!!openAssistanceRequests.size) {
      return (document.title = `!! ${i18n.t("pendingAssistanceRequests")} !!`);
    }
  }
  const whitelabel = getWhitelabel();
  document.title = whitelabel.platformName || "Web app";
}

function* setSoundSettings() {
  const user = yield select(selectCurrentUser());
  if (user?.isARC !== true) return;
  yield put(toggleDisconnectedSound(true));
}

function* downloadFile() {
  const hash = window.location.hash;
  if (hash?.indexOf("#dwnld=") !== 0) return;
  window.location.hash = "#";
  const url = hash.replace("#dwnld=", "");
  window.open(url);
  const t = getI18n().t;
  const body = t("yourFileIsDownloading");
  yield put(showAlert(body));
}

function* tryLoginPublicViaToken() {
  const currentUser = yield select(selectCurrentUser());
  if (currentUser) return;

  if (!window.location.pathname.match(/\/live-location\/.*/)) return;
  const token = window.location.pathname.split("/")[2];
  const user: User = yield api.loginPublicLiveLocationWithToken(token);
  yield put(authSuccess(user, false));
}

// Names the session and carries its human key — cosmetic, so a failure here must
// never stop the console from booting.
function* fetchSessionArc(arcId) {
  try {
    return (yield v4api.arcsDetail(arcId))?.data || null;
  } catch (e) {
    consoleWarn("Could not load the session's ARC", e);
    return null;
  }
}

// A failed listing reads as "no customers". Caught here: a 401 reaching
// bootstrapV4Native's catch would end the session.
function* ownsV4CustomerAccounts(ownId) {
  try {
    return ownsV4Accounts((yield v4api.accountsList())?.data, ownId);
  } catch (e) {
    consoleWarn("Could not list the V4 accounts for the account-manager check", e);
    return false;
  }
}

/**
 * Boots the platform shell on V4 data when the only credential is a V4 bearer
 * token. V3 REST and the V3 websocket can't authenticate such a session — the
 * isV4Native guards keep them silent.
 */
function* tryLoginV4Native() {
  // App renders a loading page while this flag is set — every exit must clear it.
  try {
    yield* bootstrapV4Native();
  } finally {
    yield put(v4NativeBootFinished());
  }
}

export function* bootstrapV4Native() {
  const currentUser = yield select(selectCurrentUser());
  if (currentUser) return;
  if (isOnV4ExcludedPath()) return;

  const v4Token = getV4Token();
  if (!v4Token) return;
  // Before identifySentry, which would name this session after a leftover adminUser.
  clearOrphanedV3Session();

  try {
    const v4User = (yield v4api.userList())?.data;
    if (!v4User?.owner_id) return;
    // The account every later V4 request defaults to — see resolveV4Scope in V4client.
    setV4OwnerAccount(v4User.owner_id);
    // A stored scope means a manager entered a sub-account — boot the shell it was entered as.
    const sessionScope = getV4SessionScope();
    // This tab's own copy: another tab may store a different scope before this one reloads.
    setV4BootScope(sessionScope);
    const accountUuid = sessionScope?.accountId || v4User.owner_id;
    let account = null;
    try {
      account = (yield v4api.accountsDetail(accountUuid))?.data;
    } catch (_) {
      // Account detail is cosmetic (name, manager flag) — proceed without it.
    }
    const managerScope = isV4ManagerBoot(sessionScope, account);

    // Scoped, `/v1/permissions` answers for the entered account alone, not its subtree.
    const permissions = managerScope
      ? yield getV4Permissions(v4Unscoped())
      : yield getV4Permissions();

    // An alarm-centre operator is defined by its rules alone — one ARC and
    // nothing else. App gives it the console off `user.isAlarmCenter`.
    const arcId = v4ArcOnlyRole(permissions);
    const arc = arcId ? yield* fetchSessionArc(arcId) : null;

    const managesAccounts = yield* v4BootManagesAccounts({
      arcId,
      sessionScope,
      account,
      permissions,
      ownId: v4User.owner_id,
    }, ownsV4CustomerAccounts);
    // Re-recorded with the shell it booted: an unmarked scope would send "back" to its portal.
    if (sessionScope?.accountId) setV4BootScope({...sessionScope, manager: !!managesAccounts});

    // countryCode needs the xx-YY shape, never a bare language code.
    const i18nLang = getI18n().language;
    // The ARC claim checks speak the human key; the grant only gives the uuid.
    const arcKey = arcId ? arc?.key || arcId : null;
    const user = new User({
      token: v4Token,
      userType: arcId
        ? UserTypes.ARC
        : managesAccounts
        ? UserTypes.ACCOUNT_MANAGER
        : UserTypes.ADMIN,
      // 3 keeps a portal user off the isPortalUser default (4), which would
      // disable every create button. 12 is UserTypes.fromUser's ARC group.
      groupId: arcId ? 12 : managesAccounts ? 6 : 3,
      lastLogin: Date.now(),
      name: [v4User.first_name, v4User.last_name].filter(Boolean).join(" "),
      email: v4User.email || "",
      // Heads the menu's first section — see endUserMenuSection.
      accountName:
        arc?.name || account?.name || sessionScope?.accountName || v4User.email || "",
      // Drives `hasAlarmCenter`; ManageARCDialog matches either form.
      arcKey,
      arcs: arcId ? uniqueArray([arcKey, arcId]) : [],
      // The uuid the ARC's alarm list is scoped by — arcKey may be the human key.
      v4ArcId: arcId,
      language: /.+-.+/.test(i18nLang || "") ? i18nLang : "en-GB",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      // V3 entitlements a V4-only account never bought — grant the ones V4
      // carries natively. Webhook only gates the two scenario surfaces, and the
      // inbound trigger stays hidden via isV3TriggerEditableInV4.
      modules: [moduleIds.Geofences, moduleIds.Teams, moduleIds.Webhook],
      // V3 functions are unreachable here — force the v4only gating on.
      v4AccountSettings: { ...(account?.settings || {}), v4only: "true" },
      v4Native: true,
      v4Permissions: permissions,
      // The account the rights are granted in — never the entered sub-account.
      v4OwnerId: v4User.owner_id,
      // Whose rules `v4Permissions` describes — see applyV4SessionGrant.
      v4UserId: v4User.id || null,
    });

    // inMemoryToken before userChanged so saveUser skips localStorage; the
    // session re-synthesizes from v4User on every boot (V4ARC pattern).
    BaseAPI.inMemoryToken = v4Token;
    yield put(userChanged(user));
    yield* seedV4PlatformData();
    // AUTH_SUCCESS never fires here (userChanged only) — run its handlers. The
    // translations loaded with this token already; a reload repeats ~240 KB.
    yield* loadV4Locales();
    // Self-gates on isARC — an alarm centre must hear that it went offline.
    yield* setSoundSettings();
    yield* identifySentry();
  } catch (e) {
    // Drop an expired token, else every boot retries this doomed bootstrap.
    if (e?.response?.status === 401) {
      clearV4Session();
      BaseAPI.inMemoryToken = null;
    }
    consoleWarn("V4-native session bootstrap failed", e);
  }
}

/**
 * Seeds the V3 reducers from V4, then fires WS_DATA_INIT to run the existing
 * V4 entity loaders — their usual trigger, the V3 websocket, never connects.
 */
function* seedV4PlatformData() {
  try {
    // The device-type registry feeds mapV4DeviceToV3 below — load it first.
    yield* fetchV4DeviceTypesSaga();

    const [devicesRes, eventsRes, contactsRes] = yield Promise.allSettled([
      v4api.devicesList(),
      v4api.historyDevicesEventsList(),
      v4api.contactsList(),
    ]);

    const devices =
      devicesRes.status === "fulfilled"
        ? (devicesRes.value?.data || []).map(mapV4DeviceToV3)
        : [];
    // No V4 device carries a position on the device endpoint, and no V3 socket
    // backfills one here — ask for every device's last fix, not just the mobile
    // ones, or hardware devices lose their location on every reload.
    yield* attachV4Locations(devices);
    yield put(dataReceived("devices", devices));

    const events =
      eventsRes.status === "fulfilled"
        ? (eventsRes.value?.data || []).map(mapV4EventToV3)
        : [];
    yield put(dataReceived("events", events));

    const contacts =
      contactsRes.status === "fulfilled"
        ? (contactsRes.value?.data || []).map(mapV4ContactToV3)
        : [];
    yield put(dataReceived("contacts", contacts));

    // The v4* slices carry the real data — these empty dispatches only flip
    // the `fetched` flags the pages spin on. Dashboards come from account
    // storage instead, via loadV4Dashboards on WS_DATA_INIT.
    for (const type of [
      "alarms",
      "teams",
      "geofences",
      "scenarios",
      "sites",
    ]) {
      yield put(dataReceived(type, []));
    }

    yield put(websocketInitialized());
  } catch (e) {
    consoleWarn("V4-native data load failed", e);
  }
}

function* fetchV4AccountSettings() {
  try {
    const user = yield select(selectCurrentUser());
    if (!user || !user.accountId) return;
    const uuid = yield call(() => getV4AccountUuid(user.accountId));
    const account = yield v4api.accountsDetail(uuid);
    const settings = account?.data?.settings;
    if (settings) {
      yield put(userChanged(user.set('v4AccountSettings', settings)));
    }
  } catch (e) {
    consoleWarn('Failed to fetch V4 account settings', e);
  }
}

function* appSaga() {
  yield takeEvery(GET_DATA, getData);
  yield takeEvery(WS_REQUEST_RELOAD_DATA, getData);
  yield takeEvery(RETRY_GET_DATA, retryGetData);
  yield takeEvery(WS_FORCE_RELOAD, forceReload);
  yield takeEvery(FORCE_RELOAD, requestForceReload);
  yield takeEvery(USER_CHANGED, saveUser);
  yield takeEvery(AUTH_SUCCESS, saveUser);
  yield takeEvery(AUTH_SUCCESS, redirectUser);
  yield takeEvery(AUTH_SUCCESS, loginWebSocket);
  yield takeEvery(AUTH_SUCCESS, setLanguage);
  yield takeEvery(AUTH_SUCCESS, loadV4Locales, { reloadTranslations: true });
  yield takeEvery(AUTH_SUCCESS, setSoundSettings);
  yield takeEvery(APP_MOUNTED, setSoundSettings);
  yield takeLatest(LANGUAGE_CHANGED, saveLanguage);
  yield takeEvery(APP_MOUNTED, redirectUser);
  yield takeEvery(APP_MOUNTED, loadWebSocket);
  yield takeEvery(APP_MOUNTED, reconnectWsAfterMount);
  yield takeEvery(APP_MOUNTED, checkForAlert);
  yield takeEvery(NEW_TOKEN, saveNewToken);
  yield takeEvery(NEW_ADMIN_TOKEN, saveNewAdminToken);
  yield takeEvery(CHECK_JWT, checkJWT);
  yield takeEvery(APP_LOGOUT, logout);
  yield takeLatest(APP_LOGIN_ADMIN, loginAdmin);
  yield takeEvery(CONNECTION_CHANGED, connectionChanged);
  yield takeEvery(ENQUEUE_SNACKBAR, doEnqueueSnackbar);
  yield takeEvery(KEEP_ALIVE, keepAlive);
  yield takeLeading(RECONNECT_WEBSOCKET, loadWebSocket);
  yield takeEvery(APP_MOUNTED, identifySentry);
  yield takeEvery(AUTH_SUCCESS, identifySentry);

  yield takeEvery(WS_DATA_ALARMS, refreshDocumentTitle);
  yield takeEvery(WS_DATA_ALARM, refreshDocumentTitle);
  yield takeEvery(WS_DATA_ASS_REQUESTS, refreshDocumentTitle);
  yield takeEvery(WS_DATA_ASS_REQUEST, refreshDocumentTitle);
  yield takeEvery(CONNECTION_CHANGED, refreshDocumentTitle);
  yield takeEvery(SET_IS_ONLINE, refreshDocumentTitle);
  yield takeEvery(APP_MOUNTED, downloadFile);
  yield takeLatest(APP_MOUNTED, tryLoginPublicViaToken);
  yield takeLatest(APP_MOUNTED, tryLoginV4Native);
  yield takeEvery(AUTH_SUCCESS, fetchV4DeviceTypesSaga);
  // Also load on WS init so the registry survives a reload — AUTH_SUCCESS only
  // fires on active login, so a restored session would otherwise have it empty.
  yield takeLatest(WS_DATA_INIT, fetchV4DeviceTypesSaga);
  yield takeEvery(WS_DATA_INIT, fetchV4AccountSettings);
  yield takeLatest(WS_DATA_INIT, fetchV4OnlyDevicesForUserSaga);
  yield takeLatest(SET_SELECTED_ACCOUNT, fetchV4OnlyDevicesSaga);

  yield all(accountSagas);
  yield all(deviceSagas);
  yield all(alarmSagas);
  yield all(dashboardSagas);
  yield all(teamSagas);
  yield all(scenarioSagas);
  yield all(geofenceSagas);
  yield all(beaconSagas);
  yield all(siteSagas);
  yield all(administrationSagas);
  yield all(eventSagas);
  yield all(assistanceRequestSagas);
  yield all(userSagas);
  yield all(deviceV4Sagas);
  yield all(alarmV4Sagas);
  yield all(scenarioV4Sagas);
}

export default appSaga;
